<script module lang="ts">
  import type { FacetType } from "@otto-ec/heureka-components/facet/type";

  import type { FilterMode, FilterOptions } from "@otto-ec/heureka-components/filter/type";
  import type { FacetValueStatus, FacetValueType } from "@otto-ec/heureka-components/facet-value/type";
  import type { FacetValueLabelType } from "@otto-ec/heureka-components/facet-value-label/type";
  import type { FacetValueGroupType } from "@otto-ec/heureka-components/facet-value-group/type";
  import { selectedCategoryPathValues } from "../categorypath/categoryPathStore";

  const defaultFacetValueLabelType: FacetValueLabelType = "text";
  const defaultFacetValueLabelOptions: Partial<Record<FilterMode, FacetValueLabelOptions>> = {
    default: { size: "125", titleSize: "100" },
    sheetSecondLevel: { fitContent: false, size: "125" },
  };
  const defaultFacetValueType: FacetValueType = "checkbox";
  const defaultFacetValueOptions: Partial<Record<FilterMode, FacetValueOptions>> = {};
  const defaultFacetValueGroupType: FacetValueGroupType = "list";
  const defaultFacetValueGroupOptions: Partial<Record<FilterMode, FacetValueGroupOptions>> = {
    default: { gridCellMinSize: "79" },
    sheetFirstLevel: { gridCellMinSize: "79" },
    sheetSecondLevel: { gridCellMinSize: "79" },
  };
  const defaultFilterTitleMode: Partial<FilterOptions> = {
    filterTitleMode: "onlyTitle",
  };
  const defaultFacetType: FacetType = "scrollable";
  const defaultFacetOptions: Partial<Record<FilterMode, FacetOptions>> = {};
</script>

<script lang="ts">
  import type { FacetValueLabelOptions } from "@otto-ec/heureka-components/facet-value-label";
  import { type FacetValue, type FacetValueOptions } from "@otto-ec/heureka-components/facet-value";
  import type { FacetValueGroupOptions } from "@otto-ec/heureka-components/facet-value-group";
  import { type Facet, activeFacet, type FacetOptions, type FacetVisibility } from "@otto-ec/heureka-components/facet";
  import type { ConfigurableFilterOptions, FilterOptions } from "@otto-ec/heureka-components/filter/type";
  import { Color, Rating, Text, TextWithLogo } from "@otto-ec/heureka-components/facet-value-label";
  import { Checkbox, Chip, Radio, Switch } from "@otto-ec/heureka-components/facet-value";
  import { Grid, List, OverflowBox, QueueBox } from "@otto-ec/heureka-components/facet-value-group";
  import { Interval, Scrollable, Simple } from "@otto-ec/heureka-components/facet";
  import type { Snippet } from "svelte";
  import { resolveFilterMode, switchFilterMode } from "@otto-ec/heureka-components/filter";

  interface Props extends ConfigurableFilterOptions {
    data: Facet;
    visibility: FacetVisibility; /*                                           */
    hasNbaValues: boolean; /*                                           */
  }

  const { data, visibility, filterTitleMode, hasNbaValues, filterConfig }: Props = $props();

  /*                                                                                             */
  /*                                                                                               */
  /*                                                        */
  const mode = $derived(resolveFilterMode(data.filterId));
  const facetLocation = $derived.by(() => {
    if (data.filterId === activeFacet.openedFilterId) {
      return activeFacet.facetLocation;
    }
    return null;
  });
  const { ...filterConfiguration } = $derived(switchFilterMode(filterConfig, mode));
  const { type: facetValueLabelType = defaultFacetValueLabelType, ...userFacetValueLabelOptions } = $derived(
    filterConfiguration.facetConfig.facetValueGroupConfig.facetValueConfig.facetValueLabelConfig,
  );
  const { type: facetValueType = defaultFacetValueType, ...userFacetValueOptions } = $derived(
    filterConfiguration.facetConfig.facetValueGroupConfig.facetValueConfig,
  );
  const { type: facetValueGroupType = defaultFacetValueGroupType, ...userFacetValueGroupOptions } = $derived(
    filterConfiguration.facetConfig.facetValueGroupConfig,
  );
  const { type: facetType = defaultFacetType, ...userFacetOptions } = $derived(filterConfiguration.facetConfig);

  /*                                 */
  const facetValueLabelOptions: FacetValueLabelOptions = $derived({
    ...defaultFacetValueLabelOptions[mode],
    ...(facetValueLabelType === "color" && { size: "100" }),
    ...userFacetValueLabelOptions,
  });

  const facetValueLabelMap: Record<FacetValueLabelType, Snippet<[FacetValue]>> = {
    color,
    rating,
    text,
    textWithLogo,
  };

  /*                           */
  const facetValueOptions: FacetValueOptions = $derived({
    ...defaultFacetValueOptions[mode],
    forceAriaLabel: facetValueLabelType === "rating",
    ...userFacetValueOptions,
  });

  const facetValueMap: Record<FacetValueType, Snippet<[FacetValue]>> = {
    checkbox,
    chip,
    radio,
    toggle,
    range,
    singleSelectRange,
  };

  /*                                 */
  const facetValueGroupOptions: FacetValueGroupOptions = $derived({
    ...defaultFacetValueGroupOptions[mode],
    item: facetValueMap[facetValueType],
    ...userFacetValueGroupOptions,
  });

  const facetValueGroupMap: Record<FacetValueGroupType, Snippet<[FacetValue[], FacetValueStatus | undefined]>> = {
    grid,
    list,
    overflowBox,
    queueBox,
  };

  /*                     */
  const facetOptions: FacetOptions = $derived({
    ...defaultFacetOptions[mode],
    contentSnippet: facetValueGroupMap[facetValueGroupType],
    ...userFacetOptions,
    visibility,
    facetLocation,
  });

  const facetMap: Record<FacetType, Snippet<[Facet]>> = {
    scrollable,
    simple,
    interval,
  };

  /*                      */
  const filterOptions: FilterOptions = $derived({
    ...defaultFilterTitleMode,
    filterTitleMode: filterTitleMode,
  });

  /*       */
  const label: Snippet<[FacetValue]> = $derived(facetValueLabelMap[facetValueLabelType]);
  const facet: Snippet<[Facet]> = $derived(facetMap[facetType]);

  /*       */
  const featureTracking = $derived(
    data.featureTracking ? { ...data.featureTracking, parentId: data.parentId } : undefined,
  );

  /*                                    */
  $effect(() => {
    if (data.name === "kategorien") {
      data.values = data.values.map((value) => ({
        ...value,
        checked: Array.from($selectedCategoryPathValues)
          .map((value) => value.id)
          .includes(value.id)
          ? "true"
          : undefined,
      }));
    }
  });
</script>

{#snippet color(facetValue: FacetValue)}
  <Color {...facetValue} {...facetValueLabelOptions} />
{/snippet}

{#snippet rating(facetValue: FacetValue)}
  <Rating {...facetValue} {...facetValueLabelOptions} />
{/snippet}

{#snippet text(facetValue: FacetValue)}
  <Text {...facetValue} {...facetValueLabelOptions} />
{/snippet}

{#snippet textWithLogo(facetValue: FacetValue)}
  <TextWithLogo {...facetValue} {...facetValueLabelOptions} bold={true} showCount={false}></TextWithLogo>
{/snippet}

{#snippet checkbox(facetValue: FacetValue)}
  <Checkbox {...facetValue} {...facetValueOptions}>
    {@render label(facetValue)}
  </Checkbox>
{/snippet}

{#snippet chip(facetValue: FacetValue)}
  <Chip {...facetValue} {...facetValueOptions} fillParent={true}>
    {@render label(facetValue)}
  </Chip>
{/snippet}

{#snippet radio(facetValue: FacetValue)}
  <Radio {...facetValue} {...facetValueOptions}>
    {@render label(facetValue)}
  </Radio>
{/snippet}

{#snippet grid(values: FacetValue[])}
  <Grid {values} {...facetValueGroupOptions} />
{/snippet}

{#snippet list(values: FacetValue[], valueStatus: FacetValueStatus | undefined)}
  <List {values} {valueStatus} {...facetValueGroupOptions} />
{/snippet}

{#snippet overflowBox(values: FacetValue[], valueStatus: FacetValueStatus | undefined)}
  <OverflowBox {values} {valueStatus} {...facetValueGroupOptions} />
{/snippet}

{#snippet queueBox(values: FacetValue[], valueStatus: FacetValueStatus | undefined)}
  <QueueBox {values} {valueStatus} {...facetValueGroupOptions} />
{/snippet}

{#snippet scrollable(facet: Facet)}
  <Scrollable
    class="find_facet find_facet--{data.selectionType}"
    {...facet}
    {...facetOptions}
    {...filterOptions}
    {hasNbaValues}
    {mode}
    parentId={facet.parentId}
  />
{/snippet}

{#snippet interval(facet: Facet)}
  <Interval
    {...facet}
    {...facetOptions}
    {...filterOptions}
    {mode}
    popularValuesSnippet={facetValueGroupMap[facetValueGroupType]}
    popularValuesType={filterConfiguration.facetConfig.facetValueGroupConfig.facetValueConfig.type}
  ></Interval>
{/snippet}

{#snippet range(facetValue: FacetValue)}
  <Chip {...facetValue} {...facetValueOptions} fillParent={true} excludeFromForm={true}>
    <Text {...facetValue} {...facetValueLabelOptions} />
  </Chip>
{/snippet}

{#snippet singleSelectRange(facetValue: FacetValue)}
  <Chip {...facetValue} {...facetValueOptions} fillParent={true} excludeFromForm={true}>
    <Text {...facetValue} {...facetValueLabelOptions} />
  </Chip>
{/snippet}

{#snippet simple(facet: Facet)}
  <Simple
    class="find_facet find_facet--{data.selectionType}"
    {...facet}
    {...facetOptions}
    {...filterOptions}
    {mode}
    parentId={facet.parentId}
  />
{/snippet}

{#snippet toggle(facetValue: FacetValue)}
  {#if facetValueLabelType === "textWithLogo"}
    <Switch {...facetValue} {...facetValueOptions}>
      {@render label(facetValue)}
    </Switch>
  {:else}
    <Switch {...facetValue} />
  {/if}
{/snippet}

{@render facet({ ...data, featureTracking })}
{#if data.explanatoryText}
  <div class="heureka_facet__explanatoryText oc-copy-50">{data.explanatoryText}</div>
{/if}

<style lang="scss">
  @use "@otto-ec/design-tokens/tokens";

  .heureka_facet__explanatoryText {
    font: tokens.$oc-semantic-font-copy-50;
    color: tokens.$oc-semantic-color-text-secondary;
    padding-top: tokens.$oc-semantic-spacing-50;
  }
</style>
